import { socialTypes } from '@constants/Socials';

/**
 * Build a complete URL from a social type and a username/path.
 * Replaces: getFullUrl, getFullUrlModelsCom, completeURL
 *
 * @param {object} socialType - A social type object from socialTypes
 * @param {string} username - The username or relative path stored in the DB
 * @param {string} [contactType] - Optional contact type for context-aware defaults (future use)
 * @returns {string} The full URL
 */
export const buildUrl = (socialType, username, contactType) => {
    if (!socialType || !username) return username || '';
    // if the user already pasted a full URL, return it as-is
    if (/^https?:\/\//.test(username)) return username;
    // special handling for social types with significant path segments (e.g. YouTube, models.com)
    if (socialType.preservePath) {
        // if the social type defines valid path segments (e.g. models.com: agency/models/people),
        // check whether the username already starts with one; if not, prepend the default
        if (socialType.validPathSegments) {
            const firstSegment = username.split('/')[0];
            if (socialType.validPathSegments.includes(firstSegment)) {
                return `${socialType.prefix}${username}`;
            }
            const defaultSegment = contactType
                ? getDefaultPathSegment(socialType, contactType)
                : socialType.defaultPathSegment;
            return `${socialType.prefix}${defaultSegment}/${username}`;
        }
        // if the social type has a default path prefix for legacy values (e.g. YouTube bare channel IDs),
        // prepend it when the stored value doesn't start with any known path prefix
        if (socialType.defaultPathPrefix && socialType.knownPathStarts) {
            const startsWithKnown = socialType.knownPathStarts.some((p) => username.startsWith(p));
            if (!startsWithKnown) {
                return `${socialType.prefix}${socialType.defaultPathPrefix}${username}`;
            }
        }
        return `${socialType.prefix}${username}`;
    }
    return `${socialType.prefix}${username}`;
};

/**
 * Derive the default path segment based on the contact type.
 * For models.com: model -> "models", agency -> "agency", client/other -> "people"
 */
const getDefaultPathSegment = (socialType, contactType) => {
    if (socialType.code !== 'ModelsCom') return socialType.defaultPathSegment;
    switch (contactType) {
        case 'agency':
            return 'agency';
        case 'model':
            return 'models';
        default:
            return 'people';
    }
};

/**
 * Parse a pasted URL and detect the social type + extract the username/path.
 * Replaces: determineSocialMedia, isWebsite, trimAt, removeAfterFWDSlash, removeAfterQuestionMark
 *
 * @param {string} input - The raw URL or username pasted by the user
 * @returns {{ socialType: object|null, username: string }}
 */
export const parseUrl = (input) => {
    if (!input) return { socialType: null, username: '' };

    for (const socialType of socialTypes) {
        if (!socialType.urlPattern) continue;
        const match = input.match(socialType.urlPattern);
        if (match) {
            let username = input.substring(match[0].length);
            if (socialType.preservePath) {
                // keep the full path intact (e.g. YouTube: "@user" or "channel/ID",
                // models.com: "agency/name" or "models/name")
                if (!socialType.preserveQuery) {
                    // strip the query string unless it carries meaningful data
                    // (e.g. YouTube "watch?v=ID", "playlist?list=ID")
                    username = username.split('?')[0];
                }
                username = username.replace(/\/+$/, '');
            } else {
                // remove query string and trailing slashes
                username = username.split('?')[0].replace(/\/+$/, '');
                // skip known path segments (e.g. "stories", "name", "in")
                const segments = username.split('/');
                if (socialType.skipPathSegments?.includes(segments[0])) {
                    username = segments[1] || '';
                } else {
                    // take only the first segment as the username
                    username = segments[0] || '';
                }
                // remove leading @ (common in TikTok, Instagram)
                username = username.replace(/^@/, '');
            }
            return { socialType, username };
        }
    }
    return { socialType: null, username: input };
};

/**
 * Check if the input looks like a URL (contains http or www).
 * Used to decide whether to run parseUrl or just clean up a plain username.
 *
 * @param {string} input
 * @returns {boolean}
 */
export const isUrl = (input) => {
    if (!input || input.length === 0) return false;
    return input.includes('http') || input.includes('www');
};

/**
 * Clean a plain username input (not a URL): remove leading @, query strings, trailing slashes.
 * Used when the user types a username without a full URL.
 *
 * @param {string} input
 * @returns {string}
 */
export const cleanUsername = (input) => {
    if (!input) return '';
    let cleaned = input.replace(/^@/, '');
    cleaned = cleaned.split('?')[0];
    cleaned = cleaned.replace(/\/+$/, '');
    return cleaned;
};

/**
 * Map a contact SubType number to a contact type string
 * used by buildUrl for context-aware path defaults (e.g. models.com).
 *
 * SubType 1 = client, 2 = agency, 3 = service, else = model
 *
 * @param {number} subType
 * @returns {string}
 */
export const getContactType = (subType) => {
    switch (subType) {
        case 1:
            return 'client';
        case 2:
            return 'agency';
        case 3:
            return 'service';
        default:
            return 'model';
    }
};

/**
 * Ensure a URL starts with https://.
 * Replaces: fixUrl (ContactSocialLink), navigateToSocial https check (SocialForm),
 *           saveSocial https check (SocialForm)
 *
 * @param {string} url
 * @returns {string}
 */
export const ensureHttps = (url) => {
    if (!url || /^https?:\/\//.test(url)) return url;
    return `https://${url}`;
};
